/**
 * BLRRD paint engine — procedural Gaussian brush mask (Phase 4.C.paint.1,
 * Wave 9.1).
 *
 * A brush mask is a square `D × D` array of coverage values α ∈ [0, 1],
 * where `D = ceil(brush_diameter)`. v1 ships a single round, soft-edged
 * profile parameterised by `hardness` ∈ [0, 1]:
 *
 *   • hardness = 0  → pure Gaussian falloff (very soft edge).
 *   • hardness = 1  → hard radial-cosine profile that reaches α≈1 across a
 *                     large plateau and falls to 0 only in the last pixel
 *                     ring (matches Photoshop's "100% hardness" feel —
 *                     pure Gaussian never reaches α=1 at the centre).
 *
 * Provenance (clean-room from public references — NO GIMP / GEGL / Krita
 * source consulted; those are GPL/LGPL):
 *   • Hughes, van Dam, McGuire et al., "Computer Graphics: Principles and
 *     Practice" 3rd ed., §10.5 "Brushing-style rendering" — stamped-brush
 *     rendering with a procedural alpha profile.
 *   • The 1D Gaussian generator is re-used verbatim from
 *     src/pipeline/capture-sharpen.js::gaussianKernel1D (the radial
 *     profile here is a 2D evaluation of the same exp(−r²/2σ²) kernel,
 *     not a separable convolution).
 *
 * Cache discipline (CRITICAL for perf — Hughes §10.5 + dossier §1.2/§9.1):
 *   Computing exp() for every pixel of every dab is O(dabs × D²) exp calls
 *   per stroke. A 256-px brush is 65 K mask pixels; 200 dabs would be 13 M
 *   exp() calls. We cache the mask once per (size, hardness) pair on
 *   `E.brushMaskCache` and reuse it across every dab. The cache key is the
 *   integer-rounded size + hardness quantised to 1/256 so sub-pixel slider
 *   noise doesn't thrash the cache.
 *
 * @module src/pipeline/paint/brush-mask
 */

/* Hardness quantisation step for cache-key stability. 1/256 ≈ 0.004 — far
 * below visual perceptibility, prevents float-noise cache thrash. */
const HARDNESS_QUANT = 256;

/* Minimum brush diameter in pixels. A 1-px brush is a single dab pixel. */
const MIN_DIAMETER = 1;

/* Maximum brush diameter we'll allocate a mask for. Matches the UI slider
 * cap; guards against an accidental gigantic allocation. */
const MAX_DIAMETER = 1024;

/**
 * Clamp a value into [lo, hi] with NaN → lo (safer than passing NaN into
 * the mask generator, which would poison every alpha value).
 */
function clamp(v, lo, hi) {
    if (!Number.isFinite(v)) return lo;
    if (v < lo) return lo;
    if (v > hi) return hi;
    return v;
}

/**
 * Quantise a brush size to an integer diameter in [MIN, MAX].
 *
 * @param {number} size  brush diameter in pixels
 * @returns {number} integer diameter
 */
export function quantizeSize(size) {
    let d = Math.round(clamp(size, MIN_DIAMETER, MAX_DIAMETER));
    if (d < MIN_DIAMETER) d = MIN_DIAMETER;
    return d;
}

/**
 * Build a round brush mask of diameter `D`, hardness `h`.
 *
 * The profile is evaluated on a normalised radius `t = r / R` where
 * `R = D/2` is the brush radius and `r` is the pixel's Euclidean distance
 * from the mask centre. We blend two profiles by hardness:
 *
 *   • soft (Gaussian):  exp(−(t / σ_n)² / 2),  σ_n = 0.5 (so the 1σ point
 *     sits at half the radius → a gentle falloff that's ~0 at the rim).
 *   • hard (smooth plateau): a smoothstep that holds α≈1 until t = h, then
 *     ramps to 0 by t = 1. At h = 1 the ramp collapses to the last pixel
 *     ring; at h = 0 it's a full-radius smoothstep.
 *
 * The final profile is `mix(soft, hard, h)` — at h=0 we get the pure
 * Gaussian, at h=1 we get the near-binary plateau. Everything outside the
 * unit radius (t > 1) is 0 so the mask is a true disc inscribed in the
 * D×D square.
 *
 * @param {number} diameter   integer diameter (use quantizeSize first)
 * @param {number} hardness   [0, 1]
 * @returns {{ size: number, hardness: number, mask: Float32Array }}
 */
export function buildBrushMask(diameter, hardness) {
    const D = quantizeSize(diameter);
    const h = clamp(hardness, 0, 1);
    const mask = new Float32Array(D * D);

    if (D === 1) {
        /* Degenerate 1-px brush: a single fully-covered pixel. */
        mask[0] = 1;
        return { size: D, hardness: h, mask };
    }

    const R = D / 2;
    /* Centre of the mask in pixel-centre coordinates. For an even D the
     * centre is between pixels (D/2 − 0.5); for odd D it's the middle
     * pixel. Using (D−1)/2 places the centre at the geometric middle of
     * the pixel grid in both cases. */
    const cx = (D - 1) / 2;
    const cy = (D - 1) / 2;

    /* Gaussian normalisation: σ in normalised-radius units. 0.5 puts the
     * 1σ ring at half the brush radius — a soft but not washed-out edge. */
    const sigmaN = 0.5;
    const invTwoSigma2 = 1 / (2 * sigmaN * sigmaN);

    for (let y = 0; y < D; y++) {
        const dy = (y - cy) / R;
        const row = y * D;
        for (let x = 0; x < D; x++) {
            const dx = (x - cx) / R;
            const t = Math.sqrt(dx * dx + dy * dy);   /* normalised radius */
            if (t >= 1) {
                mask[row + x] = 0;
                continue;
            }
            /* Soft Gaussian profile (always ≤ 1, peaks at centre). */
            const soft = Math.exp(-(t * t) * invTwoSigma2);
            /* Hard plateau profile: 1 until t = h, smoothstep down to 0
             * by t = 1. Guard h≈1 → ramp width → 0 (last ring only). */
            let hard;
            if (t <= h) {
                hard = 1;
            } else {
                const w = 1 - h;                 /* ramp width in t-space */
                const u = w > 1e-6 ? (t - h) / w : 1;  /* 0..1 across ramp */
                /* smoothstep(1 → 0): 1 − (3u² − 2u³). */
                const s = u * u * (3 - 2 * u);
                hard = 1 - s;
            }
            const a = soft + (hard - soft) * h;  /* mix(soft, hard, h) */
            mask[row + x] = a < 0 ? 0 : (a > 1 ? 1 : a);
        }
    }
    return { size: D, hardness: h, mask };
}

/**
 * Retrieve a brush mask from a cache object, rebuilding only when the
 * (size, hardness) key changes. The cache object is owned by the caller
 * (the legacy editor stores it on `E.brushMaskCache`); pass `null` to get
 * a fresh uncached mask.
 *
 * @param {{ size: number, hardness: number, mask: Float32Array }|null} cache
 * @param {number} size      brush diameter in pixels
 * @param {number} hardness  [0, 1]
 * @returns {{ cache: object, size: number, hardness: number, mask: Float32Array }}
 *   The (possibly new) cache object plus the resolved mask. The caller
 *   reassigns `E.brushMaskCache = result.cache`.
 */
export function getBrushMask(cache, size, hardness) {
    const D = quantizeSize(size);
    const h = clamp(hardness, 0, 1);
    const hKey = Math.round(h * HARDNESS_QUANT);
    if (cache && cache.size === D && cache._hKey === hKey && cache.mask instanceof Float32Array) {
        return { cache, size: D, hardness: cache.hardness, mask: cache.mask };
    }
    const built = buildBrushMask(D, h);
    const next = { size: built.size, hardness: built.hardness, _hKey: hKey, mask: built.mask };
    return { cache: next, size: built.size, hardness: built.hardness, mask: built.mask };
}

export { HARDNESS_QUANT, MIN_DIAMETER, MAX_DIAMETER };
